import { NgRedux } from '@ptl/angular-redux-store';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LoggedInUserInformationService, LOGIN_INFO_REDUCER_NAME, UserInfoStateStructure, USER_ROLE, LOGIN_STRATEGY, PtlLoginWrapperService, LINE_OF_BUSINESS_ENUM, EntitlementsHelperService } from '@ptl/account-management';
import { ContentStorageService, PtlWindowLocationService } from '@ptl/global-components';
import { CommonUtilityService } from 'src/app/shared/utility-services/common-utility.service';
import { PrepopulateUserInfoService } from 'src/app/shared/utility-services/prepopulate-userinfo.service';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { ZipCountyStateStructure } from '../interfaces/zipCountyStateStructure.interface';
import { UserInfoService } from './user-info.service';
import { Subscription } from 'rxjs';
import { REFERRAL_QUERY_PARAM_KEYS } from '../../../shared/enums/query-params.enum';
import { PasswordResetComponent } from '../components/password-reset/password-reset.component';
import { MatDialog } from '@angular/material/dialog';
import { environment } from 'src/environments/environment';
@Injectable({
  providedIn: 'root'
})
export class CommonLoginService {

  /** This'll trigger upon session timing out - ptlxhr */
  sessionTimeoutSubscription: Subscription;

  redirectPageUrls: Map<any, any>;
  postLoginPages: string;
  loginBlock = false;

  constructor(
    private reducerUtilityService: ReducerUtilityService,
    private prepopulateUserInfo: PrepopulateUserInfoService,
    private contentStorage: ContentStorageService,
    private userInfoService: UserInfoService,
    private windowLocationService: PtlWindowLocationService,
    private commonUtility: CommonUtilityService,
    private loggedInUserService: LoggedInUserInformationService,
    private snackBar: MatSnackBar,
    private matDialog: MatDialog,
    private ptlLoginWrapper: PtlLoginWrapperService,
    private entitlementsHelperService: EntitlementsHelperService
  ) { }

  /**
   * @description Will verify the user's credentials and info if they are logged in, and route them accordingly.
   * If they are not logged in, it will redirect them from any post-login pages and delete data as necessary.
   * 
   * @property postLoginPages - a string regex to identify the post-login pages
   * @property loginStrategy - the method with which to validate login/user info
   */
  async commonLoginCheck(postLoginPages: string, loginStrategy: LOGIN_STRATEGY, sessionTimeInMinutes: number): Promise<void> {
    this.postLoginPages = postLoginPages;
    
    // Make sure all our tokens and data are available on page load
    let isLoggedIn = await this.ptlLoginWrapper.synchronizeLogin(loginStrategy);

    // If we are logged in, setup session subscription based on strategy and redirect if needed
    if (isLoggedIn) {
      this.sessionTimeoutSubscription = this.ptlLoginWrapper.beginSession(sessionTimeInMinutes)
      .sessionEnded.subscribe(async (reason) => {
        // Store the last logout reason in the session for debugging real prod users
        sessionStorage.lastLogoutReason = reason;

        await this.logout();
        this.sessionTimeoutSubscription.unsubscribe();
      });

      this.handleRedirectAfterLogin();

      // If we don't redirect anywhere, we can check for a temp password reset requirement
      this.checkForTemporaryPassword();
    }
    // If we are not logged in, and on a post-login page, we need to force logout
    else if (this.commonUtility.pageMatched(this.postLoginPages)){
      let hideUnauthorizedAlert = false;
      if (loginStrategy === LOGIN_STRATEGY.EXTERNAL_SSO_LOGIN) {
        hideUnauthorizedAlert = true;
      }
      this.handleLoggedOutUser(hideUnauthorizedAlert);
    }
    // Login and Authentication pages are special cases where we want to make sure the data is wiped without redirecting
    else if (this.commonUtility.pageMatched('["/login$", "/authentication/*"]')) {
      this.userInfoService.resetStore();
    }
  }
  
  /** Logout the user on the server(s) and local browser */
  async logout() {
    await this.ptlLoginWrapper.logout();
    this.handleLoggedOutUser(true);
  }

  // Show an unauthorized message if the user was not already logged in.
  handleLoggedOutUser(isLogout?: boolean) {
    this.userInfoService.resetStoreAndRedirect(
      this.getLoginPageUrl(),
      !isLogout && this.commonUtility.pageMatched(this.postLoginPages)
    );
  }

  async handleRedirectAfterLogin(agentAbsoluteRedirectPath?: string) {
    // Notify subscribers we have logged in successfully
    this.userInfoService.emitLoginSuccess();

    if (this.commonUtility.isAgent()) {
      await this.handleAgentLoginInit(agentAbsoluteRedirectPath);
    }
    else if (this.commonUtility.isConsumer()) {
      await this.handleConsumerUserType();
    }
  }

  handleSepReviewerLogin(){
    const sitePageUrls: Map<any, any> = this.contentStorage.getContentMap('sitePageUrls');
    const loginPageUrl = this.getLoginPageUrl();
    if (sitePageUrls.get('sepReviewPageUrl')) {
      const sepReviewPageUrl: string = sitePageUrls.get('sepReviewPageUrl');
      if (window.location.pathname !== sepReviewPageUrl) {
        this.windowLocationService.goToUrl(sepReviewPageUrl);
      }
      return;
    }
  }

  private async handleCustomerLoginInit() {
    const sitePageUrls: Map<any, any> = this.contentStorage.getContentMap('sitePageUrls');
    const loginInfo: UserInfoStateStructure = this.reducerUtilityService.getReducerFromApplicationState(LOGIN_INFO_REDUCER_NAME);
    const zipCountyState: ZipCountyStateStructure = this.reducerUtilityService.getState('ZipCounty') as ZipCountyStateStructure;
    if (!zipCountyState.zipCode && loginInfo?.userInfo?.zipCode) {
      await this.commonUtility.setBrandByZipCode(loginInfo.userInfo.zipCode);
    }

    // Set member fields in prepop data
    this.prepopulateUserInfo.setPrePopFieldsCustomer(loginInfo);
    await this.prepopulateUserInfo.setMemberData(loginInfo.memberInfo);

    // TODO - Can visitors even login in? Wouldn't that make them members?
    if (loginInfo?.basicUserInfo?.userType.toLowerCase() === USER_ROLE.MEMBER) {
      const memberLandingPageUrl: string = sitePageUrls.get('memberPageUrl');
      this.windowLocationService.goToUrl(memberLandingPageUrl);
    }
    else if (loginInfo?.basicUserInfo?.userType.toLowerCase() === USER_ROLE.VISITOR) {
      const consumerLandingPageUrl: string = sitePageUrls.get('consumerPageUrl');
      this.windowLocationService.goToUrl(consumerLandingPageUrl);
    } else {
      const consumerLandingPageUrl: string = sitePageUrls.get('consumerPageUrl');
      this.windowLocationService.goToUrl(consumerLandingPageUrl);
    }
  }

  private async handleAgentLoginInit(absoluteRedirectPath?: string) {
    const sitePageUrls: Map<any, any> = this.contentStorage.getContentMap('sitePageUrls');
    const loginInfo: UserInfoStateStructure = this.reducerUtilityService.getReducerFromApplicationState(LOGIN_INFO_REDUCER_NAME);
    const loginPageUrl = this.getLoginPageUrl();

    // TODO: consumers currently just auto-login and are left on the same page, in theory. But should they also be forced somewhere similar to this?
    // NOTE: If the Agent is on the login page, and is already logged in, we should send them ahead.
    const hasSepReviewerEntitlements = await this.entitlementsHelperService.hasSepReviewerEntitlements();
    if (hasSepReviewerEntitlements && loginInfo.basicUserInfo.userType.toLowerCase() === USER_ROLE.INTERNAL && environment.initData.brand.ASPID === "MN") {
      const sepReviewDashboardUrl: string = absoluteRedirectPath || sitePageUrls.get('sepReviewPageUrl') || '/sep-review-dashboard';
      
      if (window.location.pathname != sepReviewDashboardUrl) {
        this.handleSepReviewerLogin();
      }
      return;
    }

  
    if (window.location.href.includes(loginPageUrl)) {
      const producerLandingPageUrl: string = absoluteRedirectPath || sitePageUrls.get('producerPageUrl') || '/home';
      if (window.location.pathname !== producerLandingPageUrl) {
        this.windowLocationService.goToUrl(producerLandingPageUrl);
      }
      return;
    }

    // Check for agent cubed parameters and load data if needed
    const isAgentCubed = await this.loggedInUserService.isAgentCubeAwait();
    const queryParams = this.windowLocationService.getParsedQueryParams();
    const customerId = queryParams?.[REFERRAL_QUERY_PARAM_KEYS.CUSTOMER_ID_PARAM];
    const opportunityId = queryParams?.[REFERRAL_QUERY_PARAM_KEYS.OPPORTUNITY_ID_PARAM];
    const crmOpportunityId = queryParams?.[REFERRAL_QUERY_PARAM_KEYS.CRM_OPPORTUNITY_ID_PARAM];

    if (isAgentCubed && customerId) {
      const agentCubedDemographicInfo = await this.prepopulateUserInfo.setPrePopFieldsAgentCubed(customerId, opportunityId);
      await this.commonUtility.setBrandByZipCode(agentCubedDemographicInfo.zipCode);

      this.initiateRedirect('agentCubedPageUrl', 'Agentcubed');
    }
    else if (crmOpportunityId) {
      const salesForceDemographicInfo = await this.prepopulateUserInfo.setPrePopFieldsSalesForce(crmOpportunityId);
      await this.commonUtility.setBrandByZipCode(salesForceDemographicInfo.zipCode);

      this.initiateRedirect('salesForcePageUrl', 'SalesForce');
    }
  }

  private checkForTemporaryPassword() {
    // Else we are logged in and not redirecting anywhere, so check for a temp password reset now
    const loginInfo: UserInfoStateStructure = this.reducerUtilityService.getReducerFromApplicationState(LOGIN_INFO_REDUCER_NAME);
    if (loginInfo.basicUserInfo.temppw) {
      this.matDialog.open(PasswordResetComponent, {
        width: '100vw',
        height: '100vh',
        maxWidth: '100vw'
      });
    }
  }

  private async handleConsumerUserType() {
    // NOTE: Consumers on postLoginPages should not be there, kick them out.
    // We don't have any consumer pages that are strictly post login, but there could be in the future.
    if (this.commonUtility.pageMatched(this.postLoginPages)) {
      const alertDuration = 1000; // ms
      this.snackBar.open(`This website is for Agents, Producers and Administrators only. Sending you back to login`, 'ACCESS DENIED', {
        duration: alertDuration,
        panelClass: 'negative-panel-class'
      });

      setTimeout(() => {
        this.userInfoService.resetStoreAndRedirect();
      }, alertDuration);
    }
    else {
      await this.handleCustomerLoginInit();
    }
  }

  private initiateRedirect(redirectUrlContentKey: string, label: string){
    // TODO: currently we wait 1.5 seconds to make sure content is fully loaded/stored by background scripts
    // This should be fine, but could be refactored to use some subscription from the indexDB service
    setTimeout(() => {
      this.redirectPageUrls = this.contentStorage.getContentMap('redirectPageUrls');
      let redirectUrl: string = this.redirectPageUrls.get(redirectUrlContentKey);

      if (!redirectUrl) {
        this.snackBar.open(`${label} Information found, but no redirect configuration available.`, 'CONFIG MISSING', {
          duration: 4000,
          panelClass: 'positive-panel-class'
        });
        return false;
      }

      this.snackBar.open(`${label} Information found, redirecting.`, 'OK', {
        duration: 4000,
        panelClass: 'positive-panel-class'
      });

      this.windowLocationService.goToUrl(redirectUrl);
    }, 1500);
  }

  private getLoginPageUrl() {
    const sitePageUrls: Map<any, any> = this.contentStorage.getContentMap('sitePageUrls');
    return sitePageUrls.get('loginPageUrl') || '/login';
  }

  
}

